'use es6';

import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UISection from 'UIComponents/section/UISection';
import { CANDY_APPLE, MARIGOLD, OLAF } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import { H4, H5 } from 'UIComponents/elements/headings';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { useDispatch, useSelector } from 'react-redux';
import { useApprovalConditions } from 'ui-product-approvals';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIButton from 'UIComponents/button/UIButton';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { openApprovalsTab } from 'ContentEditorUI/redux/actions/collaborationSidebarActions';
import UIIllustration from 'UIComponents/image/UIIllustration';
import { useMemo } from 'react';
import { getIsUngatedForUnifiedPublishButton } from '../../redux/selectors/authSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ICON_SIZE = 12;
const CIRCULAR_PADDING = 0.3;
const formatErrorMessage = ({
  target,
  onClickData,
  message,
  data,
  useHtml
}) => {
  const Translation = useHtml ? FormattedHTMLMessage : FormattedReactMessage;
  return {
    target,
    onClickData,
    node: /*#__PURE__*/_jsx(Translation, {
      message: message,
      options: data
    })
  };
};
const Issues = ({
  issues,
  iconBgColor,
  issueLabel,
  onIssueClick
}) => {
  if (issues.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(UISection, {
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(UIMediaObject, {
        className: "m-left-8",
        itemLeft: /*#__PURE__*/_jsx(UIIconCircle, {
          name: "warning",
          size: ICON_SIZE,
          padding: CIRCULAR_PADDING,
          color: OLAF,
          backgroundColor: iconBgColor
        }),
        children: /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: issueLabel
          })
        })
      })
    }), /*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(UIList, {
        ordered: true,
        styled: true,
        childClassName: "m-left-2 m-right-2",
        children: issues.map((issue, index) => {
          const {
            node
          } = issue;
          return /*#__PURE__*/_jsx(UILink, {
            use: "unstyled",
            onClick: () => onIssueClick(issue),
            children: node
          }, index);
        })
      })
    })]
  });
};
const PanelFooter = ({
  onClose,
  publishButtonCallback,
  publishButtonTextKey,
  disabledPublishButtonTooltipText
}) => {
  const dispatch = useDispatch();
  const onOpenApprovalsTab = () => dispatch(openApprovalsTab());
  const {
    userCanSkipThisApproval,
    isApproved
  } = useApprovalConditions();
  const isUngatedForUnifiedPublishButton = useSelector(getIsUngatedForUnifiedPublishButton);
  const canPublish = userCanSkipThisApproval || isApproved;
  if (isUngatedForUnifiedPublishButton) {
    return null;
  }
  const publishButton = /*#__PURE__*/_jsx(UIButton, {
    disabled: !canPublish,
    use: "primary",
    onClick: publishButtonCallback,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: publishButtonTextKey
    })
  });
  const disabledPublishButton = /*#__PURE__*/_jsx(UITooltip, {
    title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: disabledPublishButtonTooltipText,
      options: {
        onClick: () => {
          onClose();
          onOpenApprovalsTab();
        }
      },
      elements: {
        UILink
      }
    }),
    children: publishButton
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!canPublish ? disabledPublishButton : publishButton, /*#__PURE__*/_jsx(UIButton, {
      onClick: () => dispatch(routeToPage('preview')),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "warningsAndSuggestionsPanel.preview"
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      onClick: onClose,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "warningsAndSuggestionsPanel.cancel"
      })
    })]
  });
};
const WarningsAndSuggestionsPanel = ({
  errorLabels,
  warningLabels,
  /**
   * @deprecated use errorLabels (an instance of Issue[])
   */
  errors,
  /**
   * @deprecated use warningLabels (an instance of Issue[])
   */
  warnings,
  onIssueClick,
  onClose,
  publishButtonCallback,
  publishButtonTextKey,
  disabledPublishButtonTooltipText
}) => {
  const dispatch = useDispatch();
  const issueClickHandler = ({
    target,
    onClickData
  }) => {
    dispatch(onIssueClick(target, onClickData));
  };
  const shouldShowEmptyState = useMemo(() => {
    if (errors && errors.length > 0 || errorLabels && errorLabels.length > 0) {
      return false;
    }
    if (warnings && warnings.length > 0 || warningLabels && warningLabels.length > 0) {
      return false;
    }
    return true;
  }, [errorLabels, errors, warningLabels, warnings]);
  const emptyState = /*#__PURE__*/_jsxs("div", {
    className: "text-center p-all-6",
    children: [/*#__PURE__*/_jsx(UIIllustration, {
      name: "success-green",
      width: "30%"
    }), /*#__PURE__*/_jsxs("div", {
      className: "m-top-4 m-bottom-4",
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "warningsAndSuggestionsPanel.emptyState.title"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "warningsAndSuggestionsPanel.emptyState.body"
        })
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(UIPanel, {
    children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(UIPanelHeader, {
      children: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "approvals.warningsAndSuggestions"
        })
      })
    }), /*#__PURE__*/_jsx(UIPanelBody, {
      children: /*#__PURE__*/_jsx("div", {
        className: "m-top-8",
        children: shouldShowEmptyState ? emptyState : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Issues, {
            issues: errorLabels || errors.map(formatErrorMessage),
            iconBgColor: CANDY_APPLE,
            issueLabel: "epIssueButton.errorLabel",
            onIssueClick: issueClickHandler
          }), /*#__PURE__*/_jsx(Issues, {
            issues: warningLabels || warnings.map(formatErrorMessage),
            iconBgColor: MARIGOLD,
            issueLabel: "epIssueButton.warningLabel",
            onIssueClick: issueClickHandler
          })]
        })
      })
    }), /*#__PURE__*/_jsx(UIPanelFooter, {
      children: /*#__PURE__*/_jsx(PanelFooter, {
        publishButtonCallback: publishButtonCallback,
        publishButtonTextKey: publishButtonTextKey,
        disabledPublishButtonTooltipText: disabledPublishButtonTooltipText,
        onClose: onClose
      })
    })]
  });
};
export default WarningsAndSuggestionsPanel;